import _, { get as _get, set as _set, isNumber as _isNumber, capitalize as _capitalize } from 'lodash'
import { v4 as generateUid } from 'uuid'
import { EncodingPageLayout, ModalType, OperationEncodingTagType } from 'stylewhere/types'
import {
  ShipmentParcel,
  DecodedItemSorting,
  DecodedItem,
  ProductInfoField,
  TmrTag,
  TmrProduct as StwProduct,
  ShippingParcelChecklist,
  ConfirmModalParams,
  SortingGroupRead,
  Asn,
  ShippingParcel,
  TrmEncodingVerifyGroupedTag,
  EncodingCounters,
  GroupedItemsByProduct,
  Product
} from 'stylewhere/api'
import {
  AppStore,
  setFieldValue,
  FormSchemaField,
  ShippingOperationConfig,
  OperationReadingProvider
} from 'stylewhere/shared'
import { __, T } from 'stylewhere/shared/i18n'
import { EncodingOperationConfig, SimpleParcelListOperationConfig } from 'stylewhere/shared/RemoteOperation'

export const TEMERA_SUPPORT_EMAIL = 'support@temera.it'
export const TEMERA_SUPPORT_PHONE = '+39 0550981917'

export { hideFeedbackModal, openFeedbackModal, showToast, showToastError, hideToast } from './toast'

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function uuid() {
  return generateUid()
}

export const CUSTOM_COMMANDS_OPERATION_TYPE = 'ISO_15_AUTH'

export const getCheckListTypes = (shippingDetails: ShipmentParcel[]) => {
  const expectedItems = shippingDetails
    .map((detail) => detail.expectedCounter.items)
    .reduce((acc, curr) => acc + curr, 0)
  const expectedTags = shippingDetails.map((detail) => detail.expectedCounter.tags).reduce((acc, curr) => acc + curr, 0)
  const expectedUpcs = shippingDetails.map((detail) => detail.expectedCounter.upcs).reduce((acc, curr) => acc + curr, 0)
  const checklistTypes: ('ITEMS' | 'TAGS' | 'UPCS')[] = []
  if (expectedItems > 0) {
    checklistTypes.push('ITEMS')
  }
  if (expectedTags > 0) {
    checklistTypes.push('TAGS')
  }
  if (expectedUpcs > 0) {
    checklistTypes.push('UPCS')
  }
  return checklistTypes
}

export function askUserConfirmation(
  title: string,
  message: string,
  labelClose?: string,
  labelConfirm?: string,
  /** if present (identifierCode or productCode) show a product information box inside the confirmation modal */
  showDetailForProduct?: ConfirmModalParams['showDetailForProduct']
): Promise<boolean> {
  return new Promise((resolve) => {
    AppStore.showConfirmModal(
      title,
      message,
      () => resolve(true),
      () => resolve(false),
      labelClose,
      labelConfirm,
      showDetailForProduct
    )
  })
}

export function hideConfirmModal() {
  AppStore.hideConfirmModal()
}

export function openModal(modal: ModalType) {
  AppStore.openModal?.({ id: (+new Date()).toString(), visible: true, ...modal })
}

export function closeModal(id: string) {
  AppStore.closeModal?.(id)
}

export function truncateString(text = '', maxLength = 0) {
  if (!text || (maxLength > 0 && text.length < maxLength)) return text

  return `${text.substring(0, maxLength)}..`
}

function _getDate(timestamp?: number | string, addHourMinutes = false) {
  if (!timestamp) return undefined

  const twoDigits = (s) => `0${s}`.slice(-2)

  const date = new Date(timestamp)
  let result = `${twoDigits(date.getDate())}/${twoDigits(date.getMonth() + 1)}/${date.getFullYear()}`
  if (addHourMinutes) {
    result = `${result} ${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`
  }
  return result
}

export function getDatetime(timestamp?: number | string) {
  return _getDate(timestamp, true)
}

export function getDate(timestamp?: number | string) {
  return _getDate(timestamp)
}

export function getListFromCommaSeparatedString(text?: string) {
  return text ? text.replace(/\s/g, '').split(',') : []
}

export const getField = (object: any, path: string) => _get(object, path)

export const isNumber = (number: any) => _isNumber(number)

export const capitalize = (str: string) => _capitalize(str)

export const filterIgnoredItems = (itm) => itm.item?.status !== 'ignored' && itm.item?.status !== 'ignoredWithReason'
export const filterIgnoredEntries = (itm) => itm.status !== 'ignored' && itm.status !== 'ignoredWithReason'

export const filterIgnoredAndErrorItems = (itm) =>
  itm.item?.status !== 'error' && itm.item?.status !== 'ignored' && itm.item?.status !== 'ignoredWithReason'

export const filterErrorItems = (itm: DecodedItem) => itm.item?.status === 'error'
export const filterErrorEntries = (itm) => itm.status === 'error'

export const getCustomTranslation = (key?: string) => {
  if (!key) return ''
  return __(T.custom[key]) || key
}

export function __CAPITALIZE(txt) {
  const arr = txt.split(' ')
  for (let i = 0; i < arr.length; i++) {
    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1).toLowerCase()
  }
  return arr.join(' ')
}

export const getEpc = (item: DecodedItemSorting['item']) => {
  return item?.identifiers.find(({ type }) => type === 'UHF_TAG')?.code ?? 'n/a'
}

export const getSerial = (item?: DecodedItemSorting['item']) => {
  return (
    item?.identifiers.find(({ type, role }) => type === 'SIMPLE_ITEM_IDENTIFIER' && role === 'serial')?.code ?? 'n/a'
  )
}

export const getSimulateProductCode = () => {
  return '8057201490613'
}

export const getSimulateEpcs = () => {
  return ['3035EBC5C4314F8000D6CF74', '3035EBC5C4314F8000D6CF73']
}

export const getConveyorItemConfiguration = () => {
  return { useProductCode: true, hideIdentifiers: true, hideCqStatus: true }
}

export const sortGroupeditemList = (a: DecodedItem) => {
  if (a.item?.__unexpected) return -1
  return 0
}

export const sortGroupeditemListStatus = (a, b) => {
  if (a.item?.status === 'error') return -1
  if (b.item?.status === 'error') return 1
  if (a.item?.status === 'warning') return -1
  if (b.item?.status === 'warning') return 1
  if (a.item?.status === 'ignored') return -1
  if (b.item?.status === 'ignored') return 1
  return 0
}

export const getImbustatriceProductError = (product) => {
  let str = ''
  if (product) {
    str = product.code
    if (product.material?.value) str += ' / ' + product.material.value
    if (product.size?.value) str += ' / ' + product.size.value
    if (product.color?.value) str += ' / ' + product.color.value
    str += ' / '
  }
  return str
}

export const CONVEYOR_MIN_WIDTH_SCREEN = 800
export const KEEP_ALIVE_TIME = 1000 * 30 // 30 secondi
export const KEEP_ALIVE_CONTROL_TIME = KEEP_ALIVE_TIME * 2 // 1 minuto dopo l'ultimo keep alive ricevuto
export const MAX_RETRY_SSE = 5
export const RETRY_SSE_TIME = KEEP_ALIVE_TIME * 4 // ogni 2 minuti dopo lo scadere del KEEP_ALIVE_CONTROL_TIME prova a riallacciare sse
export const MAX_LOG_ERROR = 50
export const ERROR_DATE_FORMAT = 'HH:mm:ss'

export const checkQcStatus = () => {
  return false
}

export const isQcStatusProduct = (item) => {
  let cqStatus = 'KO'
  if (item?.attributes?.qcStatus && item.attributes.qcStatus !== 'LOADING') {
    cqStatus = item.attributes.qcStatus
  }
  return cqStatus === 'OK'
}

export const checkSelfUserAttribute = (code) => {
  return true
}

export const checkItemDecodedStatuses = (statuses, accepted) => {
  return statuses && Array.isArray(statuses) && statuses.length > 0 && accepted.includes(statuses[0])
}

export const getFormDataDescription = (formData) => {
  if (formData.note) {
    return formData.note
  } else if (formData.description) {
    return formData.description
  }
  return ''
}

export const getFieldWithAutocomplete = (schema) => {
  const field = schema.find((element) => {
    return element.autocompleteFields
  })
  return field
}

export const checkAutocompleteResult = (field, result, schema, formData) => {
  const res = {
    error: undefined,
    formData: undefined
  }
  let objField, val
  for (let a = 0; a < field.autocompleteFields.length; a++) {
    objField = schema.find((element) => {
      return element.name === (field && field.autocompleteFields ? field.autocompleteFields[a].name : '')
    })
    if (objField) {
      val = _get(result, field.autocompleteFields[a].field)
      if (val !== undefined) {
        setFieldValue(val, formData, objField as FormSchemaField)
      } else if (!objField.required) {
        setFieldValue(objField.defaultValue, formData, objField as FormSchemaField)
      } else {
        res.error = objField
      }
    } else {
      res.error = objField
    }
  }
  res.formData = formData
  return res
}

export const getProductCode = (product) => {
  if (product?.product) return product.product?.code || 'N/A'
  return product?.code ? product.code : 'N/A'
}

export const showSessionWarning = (stock_type, cqCount, option) => {
  if (option) {
    return stock_type === 'Senza Controllo Qualità' && cqCount === 0
  }
  return false
}

export const blockList = (stock_type, cqCount, option) => {
  if (option) {
    return stock_type === 'Senza Controllo Qualità' && cqCount === 0
  }
  return false
}

export const enabledWamGroupedProduct = () => {
  return false
}

export const getChecklistItemsCount = (
  items: DecodedItem<string>[],
  operation: ShippingOperationConfig,
  extension: any,
  checklist?: ShippingParcelChecklist | null,
  products?: { [upc: string]: StwProduct },
  productKey?: string
) => {
  const counter = {
    detecteds: 0,
    expecteds: 0,
    unexpecteds: 0,
    overQty: 0,
    missings: 0
  }
  const filterItems = items.filter(filterIgnoredAndErrorItems)
  if (operation.hasChecklist && checklist && checklist !== null) {
    const parcelType = checklist ? checklist.type : 'CHECKLIST_BY_ITEM'
    const grouped = OperationReadingProvider.groupItems(
      items,
      ['MIXED_CHECKLIST', 'CHECKLIST_BY_PRODUCT'].includes(parcelType),
      operation,
      extension,
      checklist,
      products,
      false,
      productKey
    )
    for (let g = 0; g < grouped.length; g++) {
      counter.detecteds += grouped[g].detected
      counter.expecteds += grouped[g].expected
      counter.unexpecteds += grouped[g].unexpected
      if (grouped[g].added) {
        counter.overQty +=
          grouped[g].detected - grouped[g].unexpected > grouped[g].expected
            ? grouped[g].detected - grouped[g].unexpected - grouped[g].expected
            : 0
      } else {
        counter.overQty += grouped[g].detected > grouped[g].expected ? grouped[g].detected - grouped[g].expected : 0
      }
      counter.missings += grouped[g].expected > grouped[g].detected ? grouped[g].expected - grouped[g].detected : 0
    }
  } else {
    counter.detecteds = filterItems.reduce(
      (acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, extension),
      0
    )
  }
  return counter
}

/**
 * Calcola le quantità già lette per prodotto negli altri parcels (escluso quello corrente)
 * @param otherParcelsReadings Letture degli altri parcels
 * @param operation Configurazione dell'operazione
 * @param extension Estensione per l'operazione
 * @param productKey Chiave per identificare il prodotto ('code' o 'id', default 'code' quando checklistMode === 'shipment')
 * @returns Oggetto con product code come chiave e quantità già letta come valore
 */
export const getOtherParcelsReadingsByProduct = (
  otherParcelsReadings: DecodedItem<string>[],
  operation: ShippingOperationConfig,
  extension: any,
  productKey?: string
): { [productCode: string]: number } => {
  const readingsByProduct: { [productCode: string]: number } = {}

  if (!otherParcelsReadings || otherParcelsReadings.length === 0) {
    return readingsByProduct
  }

  const productKeyFind = productKey || (operation?.options?.checklistMode === 'shipment' ? 'code' : 'id')
  const filterItems = otherParcelsReadings.filter(filterIgnoredAndErrorItems)

  filterItems.forEach((decodedItem, index) => {
    if (!decodedItem.item?.product) {
      return
    }

    const product = decodedItem.item.product
    const productCode = _get(product, productKeyFind)

    if (!productCode) {
      return
    }

    const itemIntrinsicQuantity = OperationReadingProvider.getItemIntrinsicQuantity(decodedItem, extension)

    if (readingsByProduct[productCode]) {
      readingsByProduct[productCode] += itemIntrinsicQuantity
    } else {
      readingsByProduct[productCode] = itemIntrinsicQuantity
    }
  })

  return readingsByProduct
}

export const validateChecklistReadings = (
  items: DecodedItem<string>[],
  operationConfig: ShippingOperationConfig,
  extension?: any,
  checklist?: ShippingParcelChecklist | null,
  products?: { [upc: string]: StwProduct }
) => {
  const result: {
    validationResult: 'OK' | 'WARNING' | 'KO'
    message: string
  } = { validationResult: 'OK', message: '' }

  const tempMessages: string[] = []

  const { missings, overQty, unexpecteds } = getChecklistItemsCount(
    items,
    operationConfig,
    extension,
    checklist,
    products
  )
  if (missings === 0 && overQty === 0 && unexpecteds === 0) return result

  if (missings > 0) {
    if (operationConfig.canConfirmWithMissing === 'no') result.validationResult = 'KO'
    else if (operationConfig.canConfirmWithMissing === 'withWarning') result.validationResult = 'WARNING'
    tempMessages.push(__(T.messages.checklist_missings))
  }
  if (overQty > 0) {
    if (result.validationResult !== 'KO') {
      if (operationConfig.canConfirmWithOverQuantity === 'no') result.validationResult = 'KO'
      else if (operationConfig.canConfirmWithOverQuantity === 'withWarning') result.validationResult = 'WARNING'
    }
    tempMessages.push(__(T.messages.checklist_overqty))
  }
  if (unexpecteds > 0) {
    if (result.validationResult !== 'KO') {
      if (operationConfig.canConfirmWithUnexpected === 'no') result.validationResult = 'KO'
      else if (operationConfig.canConfirmWithUnexpected === 'withWarning') result.validationResult = 'WARNING'
    }
    tempMessages.push(__(T.messages.checklist_unexpecteds))
  }
  if (tempMessages.length > 0) {
    result.message = `${__(T.messages.checklist_there_are)} ${tempMessages.join(', ')}.`
  }
  if (result.validationResult === 'WARNING') result.message += ` ${__(T.messages.checklist_confirm_mismatch_question)}`
  return result
}

export const getEncodingProductFields = () => {
  const productFields: ProductInfoField[] = [
    { path: 'style.value', label: __(T.misc.style) },
    { path: 'material.value', label: __(T.misc.material) },
    { path: 'color.value', label: __(T.misc.color) },
    { path: 'size.value', label: __(T.misc.size) }
  ]
  return productFields
}

export const getScanIdentifierConfig = (operation) => {
  const scanIdentifier: 'rfid' | 'rfidAndSerial' | 'serial' = operation.options?.scanIdentifier ?? 'rfid'
  return {
    rfid: ['rfid', 'rfidAndSerial'].includes(scanIdentifier),
    serial: ['rfidAndSerial', 'serial'].includes(scanIdentifier)
  }
}

export const isSkipValidation = (operation) => {
  return !!operation.options && operation.options.skipValidation === 'true'
}

export const enabledDisassociateItemTag = () => {
  return true
}

export const enabledUtilities = () => {
  return true
}

export const encodingComputeTagsEntityCode = (response, initialType, formSchemaData) => {
  if (initialType === 'wam') return formSchemaData.wam || ''
  return response?.item?.product?.code ?? ''
}

export const isPreEncodingWorkOrder = (operation) =>
  operation?.options?.initialType === 'workOrder' && operation?.options?.isPreEncoding === 'true'

export const parseProductionOrderRowEanAddonInput = (input?: string) => {
  const value = `${input ?? ''}`.trim()
  const productCode = value.slice(0, 13)
  const addon = value.slice(13)

  if (productCode.length !== 13 || addon.length === 0) {
    return undefined
  }

  return {
    productCode,
    attributes: { addon }
  }
}

export const enabledEanAndAddon = (operation?: { options?: any }) => {
  return operation?.options?.customExtensionCode === 'otbDfEncodingByOrderRow'
}

export const getInitialType = (operation) => {
  let type = operation.options?.initialType ?? 'product'
  if (!enabledWamGroupedProduct() && type === 'wam') {
    type = 'product'
  }
  if (type === 'workOrder' && !isPreEncodingWorkOrder(operation)) {
    // differentiate between Prada work orders and actual ones
    type = 'order'
  }
  return type
}

export const getInitialTypeOptions = (operation) => {
  const type = operation.options?.initialType ?? 'product'
  const initialTypeOptions: { productionOrdersParams?: Record<string, string> } = {}
  if (type === 'workOrder') {
    initialTypeOptions.productionOrdersParams = {
      orderType: 'WORKORDER'
    }
  }
  return initialTypeOptions
}

export const getEncodingBlockingErrors = (enableUhfWrite = 'no') => {
  const blockedErrors = ['WRONG_PRODUCTION_ORDER', 'WRONG_PLACE', 'PRODUCTION_ORDER_ROW_CREATION_ERROR']
  if (enableUhfWrite === 'gs1') {
    blockedErrors.push('MISSING_EAN_ATTRIBUTE')
  }
  return blockedErrors
}

export const BLOCKED_ERRORS = ['EXTRA_TAG']
export const MAX_WRITE_ATTEMPT = 3
export const MAX_KILL_TAG_ATTEMPT = 3

export const isExpectOnlyMandatoryIdentifiers = (operation) => {
  return operation.options?.expectOnlyMandatoryIdentifiers
    ? operation.options.expectOnlyMandatoryIdentifiers === 'true'
    : false
}

export const getCanForceAssocation = (operation) => {
  return operation.options?.canForceAssociation ?? 'no'
}

export const getUhfTagToWriteRole = (operation, itemConfigurationCode?: string) => {
  if (!itemConfigurationCode) return undefined
  return operation.options?.[`uhfTagToWriteRole_${itemConfigurationCode}`]
}

export const getEnableUhfWrite = (operation) => {
  return operation.options?.enableUhfWrite ?? 'no'
}

export const getAskUserInputEveryTime = (operation) => {
  const askUserInputEveryTime = operation.options?.askUserInputEveryTime ?? 'false'
  return askUserInputEveryTime === 'true'
}

export const getStartAntennaOrder = (operation: EncodingOperationConfig): OperationEncodingTagType[] => {
  switch (operation.options.startAntennaOrder) {
    case 'nfc_uhf':
      return ['NFC', 'UHF']
    case 'uhf_nfc':
      return ['UHF', 'NFC']
    default:
      return ['ALL']
  }
}

export const isValidOperationEncodingTagType = (option: OperationEncodingTagType): boolean => {
  if (!option) return false
  const ALL_TAG_TYPES: OperationEncodingTagType[] = ['ALL', 'NFC', 'UHF']
  return ALL_TAG_TYPES.includes(option)
}

export const getTagType = (tag: TmrTag) => {
  let identifierType = 'UHF_TAG'
  if (tag?.uid) identifierType = 'NFC_TAG'
  if (tag?.barcode) identifierType = 'SIMPLE_ITEM_IDENTIFIER'

  return identifierType
}

export const hasAsyncConfirm = (operation) => {
  return operation.executionModeForSyncConfirmation === 'async'
}

export const getEncodingAssociationStatus = (encodingCreateResponse, encodingValidationResponse) => {
  let associationStatus: 'TO_BE_READ' | 'CONFIRMED' | 'ERROR' | 'PROCESSING' | 'IGNORED' = 'TO_BE_READ'
  if (!encodingCreateResponse) associationStatus = 'TO_BE_READ'
  else {
    if (
      encodingCreateResponse &&
      (!encodingCreateResponse.errors || (encodingCreateResponse.errors && encodingCreateResponse.errors.length === 0))
    )
      associationStatus = 'CONFIRMED'
    if (
      (encodingCreateResponse?.errors ?? encodingValidationResponse?.errors ?? []).filter(
        (e) => e.errorCode !== 'Missing Mandatory Identifier'
      ).length > 0
    )
      associationStatus = 'ERROR'
  }
  return associationStatus
}

export const getSseEndpoint = (config) => {
  let endpoint = config.endpoint
  if (endpoint[endpoint.length - 1] === '/') {
    endpoint = endpoint.substring(0, endpoint.length - 1)
  }
  return endpoint + '/api/v1/sse/getData/asyncExecutionOutcome/' + AppStore.loggedUser?.id
}

export const checkItemsCounterError = (items) => {
  const errorItems = items.filter((itm) => itm.item?.status === 'error')
  const warningItems = items.filter((itm) => itm.item?.status === 'warning')
  const ignoredItems = items.filter((itm) => itm.item?.status === 'ignored')
  const ignoredWithReasonItems = items.filter((itm) => itm.item?.status === 'ignoredWithReason')
  return errorItems.length + warningItems.length + ignoredItems.length + ignoredWithReasonItems.length > 0
}

export const getAutomaticRestartAntenna = (operation) => {
  return operation.options?.automaticRestartAntenna === 'true'
}

export const isModalError = (operation) => {
  return operation.errorMessageView && operation.errorMessageView === 'modal'
}

export function arraysContainSameElements(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) return false
  const sortedArr1 = arr1.toSorted((a, b) => a.localeCompare(b))
  const sortedArr2 = arr2.toSorted((a, b) => a.localeCompare(b))
  return sortedArr1.join(',') === sortedArr2.join(',')
}

type ParsedSizeLabel = {
  numericValue?: number
  hasPlusSuffix: boolean
}

const SIZE_LABEL_PATTERN = /^(\d+(?:[.,]\d+)?)(\+)?$/

/**
 * Parse size labels such as "7", "7+", "10" into a numeric value and a boolean indicating if the label has a plus suffix.
 */
const parseSizeLabel = (label: string): ParsedSizeLabel => {
  const match = SIZE_LABEL_PATTERN.exec(label.trim())
  if (!match) return { hasPlusSuffix: false }
  return {
    numericValue: Number.parseFloat(match[1].replace(',', '.')),
    hasPlusSuffix: !!match[2]
  }
}

/** Numeric-aware compare for size labels such as "7", "7+", "10". Falls back to localeCompare. */
export const compareSizeLabels = (a: string, b: string): number => {
  const parsedA = parseSizeLabel(a)
  const parsedB = parseSizeLabel(b)

  if (parsedA.numericValue !== undefined && parsedB.numericValue !== undefined) {
    if (parsedA.numericValue !== parsedB.numericValue) {
      return parsedA.numericValue - parsedB.numericValue
    }
    if (parsedA.hasPlusSuffix !== parsedB.hasPlusSuffix) {
      return parsedA.hasPlusSuffix ? 1 : -1
    }
    return 0
  }

  if (parsedA.numericValue !== undefined) return -1
  if (parsedB.numericValue !== undefined) return 1
  return a.localeCompare(b)
}

export const decToHex = (dec: number): string => {
  return dec.toString(16)
}

export const hexToString = (hex: string): string => {
  let str = ''
  for (let i = 0; i < hex.length; i += 2) {
    str += String.fromCharCode(parseInt(hex.substr(i, 2), 16))
  }
  return str
}

export const bytesToHex = (bytes: any) => {
  const hex: string[] = []
  for (let i = 0; i < bytes.length; i++) {
    hex.push((bytes[i] >>> 4).toString(16))
    hex.push((bytes[i] & 0xf).toString(16))
  }
  return hex.join('')
}

export const hexToBytes = (hex: string) => {
  const bytes: number[] = []
  for (let c = 0; c < hex.length; c += 2) {
    bytes.push(parseInt(hex.substr(c, 2), 16))
  }
  return bytes
}

export const reverseString = (str: string) => {
  let result = ''
  for (let i = 0; i < str.length; i += 2) {
    result = str.substr(i, 2) + result
  }
  return result
}

export const putSpacesEveryByte = (str: string) => {
  return str.match(/.{1,2}/g)?.join(' ') ?? ''
}

export const splitAndConvertASCII = (input) => {
  let result = ''
  for (let i = 0; i < input.length; i += 2) {
    result += String.fromCharCode(parseInt(input.slice(i, i + 2), 16))
  }
  return result
}

export const handleDownload = (fileName: string, content: string, mimeType = 'text/plain') => {
  const text = content
  const element = document.createElement('a')
  const file = new Blob([text], { type: mimeType })
  element.href = URL.createObjectURL(file)
  element.download = fileName
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
}

export const getSortingLabel = (currentSortingGroupRead?: SortingGroupRead) => {
  if (currentSortingGroupRead?.assignedEntry?.sortingGroup) {
    const { code, description } = currentSortingGroupRead.assignedEntry.sortingGroup

    if (description && description !== '' && description !== null) {
      return `${code} - ${description}`
    }

    return code
  }

  return '---'
}

export const hasChecklist = (operation, data?: Asn | ShippingParcel) => {
  let checklist = operation && 'hasChecklist' in operation && operation.hasChecklist
  if (checklist && data?.checklistId) checklist = false
  return checklist
}

const getWithUserConfirmationMessageBase = (errors, response?): [string, any] => {
  let errorCheck = errors.find(
    (e) => e.errorCode === 'TAG_ALREADY_ASSOCIATED' || e.errorCode === 'ALREADY_ASSOCIATED_TAG'
  )
  if (errorCheck) {
    return [__(T.error.force_tag_associated), errorCheck]
  }
  errorCheck = errors.find((e) => e.errorCode === 'WORKORDER_MISMATCH')
  if (errorCheck) {
    return [__(T.error.workorder_mismatch), errorCheck]
  }
  errorCheck = errors.find((e) => e.errorCode === 'MISSING_MANDATORY_QC')
  if (errorCheck) {
    return [__(T.error.missing_mandatory_qc), errorCheck]
  }
  errorCheck = errors.find((e) => e.errorCode === 'WRONG_QC_STATUS')
  if (errorCheck) {
    return [__(T.error.wrong_qc_status), errorCheck]
  }
  errorCheck = errors.find((e) => e.errorCode === 'PRODUCTIONORDERROW_MISMATCH')
  if (errorCheck) {
    return [__(T.error.productionorderrow_mismatch), errorCheck]
  }
  errorCheck = errors.find((e) => e.errorCode === 'WRONG_QC_SIZE')
  if (errorCheck) {
    if (response?.item?.product?.size?.value && errorCheck?.payload?.manualSize) {
      return [
        __(T.error.wrong_qc_size_payload, {
          productSize: response.item.product.size?.value || 'n/a',
          manualSize: errorCheck.payload.manualSize || 'n/a'
        }),
        errorCheck
      ]
    }
    return [__(T.error.wrong_qc_size), errorCheck]
  }
  errorCheck = errors.find((e) => e.errorCode === 'WRONG_QC_MODEL')
  if (errorCheck) {
    return [__(T.error.wrong_qc_model), errorCheck]
  }
  return [__(T.error.tags_errors), {}]
}

export const getWithUserConfirmationMessage = (errors, response?) => {
  const [baseMessage, foundError] = getWithUserConfirmationMessageBase(errors, response)

  const errorMessageBE = foundError?.payload?.errorMessage ?? ''

  if (errorMessageBE) return `${baseMessage} \n ${errorMessageBE}`

  return baseMessage
}

export const getAsnParcelStatusChip = (status): 'warning' | 'error' | 'default' | 'success' => {
  switch (status) {
    case 'INBOUND_ERROR':
    case 'OUTBOUND_ERROR':
      return 'error'

    case 'RECEIVE_AWAIT':
    case 'SEND_AWAIT':
    case 'DRAFT':
      return 'warning'

    case 'READY_TO_RECEIVE':
    case 'RECEIVED':
    case 'FORCE_RECEIVED':
    case 'PACKED':
      return 'success'

    case 'IN_INBOUND':
    case 'READY_TO_SEND':
    case 'IN_TRANSIT':
    default:
      return 'default'
  }
}

export const getTimeoutValidateTags = (operation, removeIgnoredTags = false) => {
  const { options } = operation
  let timeoutValidateTags = options.timeoutValidateTags ?? 700
  //if enabled ignoreUnknownTags and time is 0 then set time to 700 as default
  if (removeIgnoredTags && timeoutValidateTags !== undefined && parseInt(timeoutValidateTags + '', 10) === 0)
    timeoutValidateTags = 700
  return timeoutValidateTags
}

export const getEncodingTagPassword = (operation): string[] => {
  const password = operation.options?.encodingTagPassword
  if (Array.isArray(password)) return password.filter((p) => !!p)
  return password
    ? password
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
    : []
}

export const getLockTagOnWriteFailure = (operation: EncodingOperationConfig) => {
  return !!(operation.options?.lockTagOnWriteFailure && operation.options.lockTagOnWriteFailure === 'true')
}

export const getCheckTagAfterWrite = (operation: EncodingOperationConfig) => {
  return !!(operation.options?.checkTagAfterWrite && operation.options.checkTagAfterWrite === 'true')
}

export const getAutocompletePostData = (endpoint, params) => {
  if (endpoint.includes('?')) {
    const arr = endpoint.split('?')
    if (arr.length > 0) {
      const additionalParam = JSON.parse(
        '{"' + decodeURI(arr[1]).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}'
      )
      const flattenedAdditionalParam = _.transform(
        additionalParam,
        (result, value, key) => {
          _.set(result, key, value)
        },
        {}
      )

      params = {
        ...params,
        ...flattenedAdditionalParam
      }

      if (params.attributes && params.search) {
        params.attributes.search = params.search
      }
    }
  }
  return params
}

export const formatTestId = (value: string | number | undefined): string => {
  if (!value) return ''
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // sostituisce uno o più spazi con un singolo trattino
    .replace(/[^a-z0-9-]/g, '') // rimuove tutti i caratteri non alfanumerici e non trattini
}

export function getCustomErrorCodes(status?: number): { [key: string]: string } {
  switch (status) {
    default:
      return {}
  }
}

export const getLanguages = () => {
  return [
    { label: 'English', value: 'en' },
    { label: 'Italiano', value: 'it' },
    { label: 'Français', value: 'fr' },
    { label: 'Español', value: 'es' },
    { label: 'Japanese', value: 'ja' },
    { label: 'Chinese (Traditional)', value: 'zh' },
    { label: 'Chinese (Simplified)', value: 'zh_cn' },
    { label: 'Portoghese ( Brasiliano )', value: 'br' }
  ]
}

export const isSuccessEncodingCreate = (response) => {
  return response && (!response.errors || (response.errors && response.errors.length === 0))
}

export const canKillToAssociateItem = () => false

export const isEncodingErrorCheckingByRole = () => false

export const encodingStartAntennaAfterSimpleItemIdentifier = () => false

export const showCountersOnEncodingVerify = (initialType: string, operation: EncodingOperationConfig) => false

export const showCountersOnEncoding = (initialType: string, counters: EncodingCounters) => true

export const getGroupedKillTags = (tags, excludeGroup = false, useEmptyGroup = false) => {
  const grouped: TrmEncodingVerifyGroupedTag[] = []
  tags
    .filter((t) => !!t.code)
    .map((tg) => {
      const find =
        !excludeGroup && (useEmptyGroup || (!useEmptyGroup && !!tg.group)) && tg.group !== null
          ? grouped.find((g) => g.group === tg.group)
          : undefined
      if (find) {
        if (!find.associate && tg.associate) find.associate = tg.associate
        find.tags.push(tg)
      } else {
        grouped.push({
          code: tg.code,
          group: tg.group,
          tokill: tg.tokill,
          associate: tg.associate,
          tags: [tg]
        })
      }
    })
  return grouped
}

export const getReverseUid = (uid) => {
  const result = {
    reversed: '',
    success: false
  }
  const match = uid.match(/.{2}/g)
  if (!match) {
    return result
  }
  result.reversed = match.reverse().join('').toUpperCase()
  result.success = true
  return result
}

export const getEncodedOrder = (counters: EncodingCounters) => {
  if (counters?.order) {
    let total = counters?.order?.pending + counters?.order?.encoded
    if (total < 0) total = 0
    return total
  }
  return 0
}

export const getEncodedParent = (counters: EncodingCounters) => {
  if (counters?.parent) {
    let total = counters.parent.pending + counters.parent.encoded
    if (total < 0) total = 0
    return total
  }
  return 0
}

export const getProductKeyAggregate = (enableBarcodeJde: boolean = true) => {
  return undefined
}

export const getEnableBarcodeJDE = (operation?: { attributes?: any }): boolean => {
  return false
}

export const getEncodedOrderRow = (counters: EncodingCounters) => {
  if (counters?.row) {
    let total = counters.row.pending + counters.row.encoded
    if (total < 0) total = 0
    return total
  }
  return 0
}

export const getExpectedOrder = (counters: EncodingCounters) => {
  const expected = counters?.order?.expected ?? 0
  return expected < 0 ? 0 : expected
}

export const getExpectedParent = (counters: EncodingCounters) => {
  const expected = counters?.parent?.expected ?? 0
  return expected < 0 ? 0 : expected
}

export const getExpectedRow = (counters: EncodingCounters) => {
  const expected = counters?.row?.expected ?? 0
  return expected < 0 ? 0 : expected
}

export const isCheckEncodedQuantity = (operation: EncodingOperationConfig) => {
  return operation.options.checkEncodedQuantity && operation.options.checkEncodedQuantity === 'true'
}

export const isCanEncodeWithOverQuantity = (operation: EncodingOperationConfig) => {
  return operation.options.canEncodeWithOverQuantity && operation.options.canEncodeWithOverQuantity === 'yes'
}

export const isOrderCompleted = (
  counters: EncodingCounters,
  checkEncodedQuantity: boolean,
  canEncodeWithOverQuantity: boolean
) => {
  if (!checkEncodedQuantity) return false
  if ((counters?.order?.expected ?? 0) + (counters?.order?.extra ?? 0) > 0) {
    const expected = (counters?.order?.expected ?? 0) + (canEncodeWithOverQuantity ? (counters?.order?.extra ?? 0) : 0)
    return getEncodedOrder(counters) >= expected
  }
  return false
}

export const isOrderRowCompleted = (
  counters: EncodingCounters,
  checkEncodedQuantity: boolean,
  canEncodeWithOverQuantity: boolean
) => {
  if (!checkEncodedQuantity) return false
  if ((counters?.row?.expected ?? 0) + (counters?.row?.extra ?? 0) > 0) {
    const expected = (counters?.row?.expected ?? 0) + (canEncodeWithOverQuantity ? (counters?.row?.extra ?? 0) : 0)
    return getEncodedOrderRow(counters) >= expected
  }
  return false
}

export const getEncodingPageLayout = (): EncodingPageLayout => {
  return {
    sideBarWidth: 500,
    productBoxPaddingCard: '10px 20px 10px 20px',
    productImage: {
      width: 120,
      height: 120,
      maxHeightContainer: 260
    }
  }
}

export const getUseTidTargetDuringWrite = (operation: EncodingOperationConfig) => {
  return (
    operation.options &&
    ((operation.options.useTidTargetDuringWrite && operation.options.useTidTargetDuringWrite === 'true') ||
      !operation.options.useTidTargetDuringWrite)
  )
}

export const getDesiredCode = (res) => {
  let desiredCode = ''
  if (res && res.length > 0) {
    if (res.length > 1) {
      const find = res.find((r) => r.identifierType === 'UHF_TAG')
      if (find) desiredCode = find.desiredCode
    } else {
      desiredCode = res[0].desiredCode
    }
  }
  return desiredCode
}

/**
 * Default number of product attributes to show in the product info box.
 * This can be used to limit the number of attributes displayed in the UI.
 * If the product has more attributes, they will be hidden.
 * @default 4
 */
export const DEFAULT_PRODUCT_ATTRIBUTES = 4

export const withFetchReplacedIdentifierOnTagInfo = false

export const isProductionEnvironment = () => {
  const host = window.location.host
  return (
    host.indexOf('localhost') === -1 &&
    host.indexOf('-dev') === -1 &&
    host.indexOf('-qty') === -1 &&
    host.indexOf('-test') === -1 &&
    host.indexOf('-int') === -1 &&
    host.indexOf('-prp') === -1
  )
}

/**
 * Post-processa i rows raggruppati per correggere il detected quando necessario.
 * Questa funzione può essere sovrascritta nelle customizzazioni per implementare
 * logiche specifiche (es. quando prodotti vengono raggruppati per productKey).
 *
 * @param rows - I rows risultanti da OperationReadingProvider.groupItems
 * @param checklist - Il checklist contenente checklistProducts
 * @param products - L'oggetto products con i dettagli dei prodotti
 * @param productKey - La chiave usata per il raggruppamento (es. 'attributes.barcodeJDE.value')
 * @returns I rows con detected eventualmente corretto
 */
export function fixDetectedForProductKey(
  rows: GroupedItemsByProduct[],
  checklist?: ShippingParcelChecklist | null,
  products?: { [upc: string]: StwProduct },
  productKey?: string
): GroupedItemsByProduct[] {
  return rows
}

/**
 * Array di path di campi specifici da mostrare nella lista degli ASN (componente AsnItem.tsx)
 */

export interface CustomFieldKeyTitle {
  key: string
  title?: string
}

export function showSpecificAsnFields(
  asn: Asn,
  operationConfig?: ShippingOperationConfig | SimpleParcelListOperationConfig
): CustomFieldKeyTitle[] | undefined {
  return undefined
}

export const canDeleteParcel = (
  parcel?: ShippingParcel,
  operationConfig?: ShippingOperationConfig | SimpleParcelListOperationConfig
) => {
  if (operationConfig && parcel) {
    if (operationConfig.type === 'OUTBOUND') {
      return ['IN_OUTBOUND', 'READY_TO_SEND', 'OUTBOUND_ERROR', 'DRAFT'].includes(parcel.status)
    } else if (operationConfig.type === 'INBOUND') {
      return ['IN_INBOUND', 'READY_TO_RECEIVE', 'INBOUND_ERROR', 'DRAFT'].includes(parcel.status)
    } else if (operationConfig.type === 'PACKING') {
      return ['IN_INBOUND', 'IN_OUTBOUND', 'DRAFT'].includes(parcel.status)
    }
  }
  return false
}

export const getDefaultFilterShipment = (
  operationConfig: ShippingOperationConfig,
  params: Record<string, any>,
  packingStatusDefault: any
) => {
  const isPacking = operationConfig.type === 'PACKING'
  const data = {
    tab: 'parcel',
    search: '',
    filters: {}
  }
  const keys = Object.keys(params)
  if (keys.length > 0) {
    keys
      .filter((k) => ['statuses', 'creationDateStart', 'creationDateEnd', 'search', 'tab'].includes(k))
      .forEach((d) => {
        if (d === 'tab') {
          data.tab = params[d]
        } else if (d === 'search') {
          data.search = params[d]
        } else if (['creationDateStart', 'creationDateEnd'].includes(d)) {
          _set(data.filters, d, params[d].replace(' ', '+'))
        } else {
          _set(data.filters, d, params[d])
        }
      })
  } else {
    data.filters = isPacking ? packingStatusDefault : undefined
  }
  return data
}

export const getProductLabel = (product: Product | StwProduct) => {
  const labelArr: string[] = []
  if (product) {
    const fields = ['style', 'material', 'color', 'size']
    fields.forEach((key) => {
      let value = '---'
      if (product[key]) {
        value =
          product[key]?.description && product[key].description != null
            ? product[key]?.description
            : product[key]?.value
              ? product[key]?.value
              : '---'
      }
      labelArr.push(value)
    })
  }
  const label = labelArr.join(' / ')
  return label === '--- / --- / --- / ---' ? '---' : label
}

export function urlParamsToObject(urlString: string): Record<string, string | number> {
  const url = new URL(urlString)
  const result: Record<string, string | number> = {}
  for (const [key, value] of url.searchParams) {
    const num = Number(value)
    result[key] = value !== '' && !Number.isNaN(num) ? num : value
  }
  return result
}

export const buildGridTemplate = (columns) => {
  return columns
    .map((col, i) => {
      if (i === 0) {
        // 👇 prima colonna stabile
        return 'minmax(150px, 2fr)'
      }

      if (col.maxWidth) {
        return `minmax(0, ${col.maxWidth}px)`
      }

      return 'minmax(0, 1fr)'
    })
    .join(' ')
}
